import { useEffect, useId, useRef, useState, type Dispatch } from "react"
import { createPortal } from "react-dom"
import { INJURY_ZONES, getInjuryZoneLabel, INJURY_STATUS_LABELS, GENDER_LABELS } from "@/data/labels"
import { mailtoHref } from "@/data/contact"
import type { InjuryZone } from "@/lib/supabase"
import {
  ADVANCED_HOME_NOTE, BEGINNER_EXPERIENCE_OPTIONS, GOALS_BY_LEVEL, INJURY_CONSENT_DETAILS,
  INJURY_CONSENT_LABEL, LEVELS, METRICS, PAIN_RECOMMENDATION, PLACES, TRIAL_ALREADY_USED,
  type ParcoursTexts,
} from "./content"
import { CheckMark, Choice, Eyebrow, Lead, ScreenTitle } from "./components"
import { NumberWheel } from "@/components/shared/NumberWheel"
import {
  allZonesHaveStatus, hasActivePain, programmeLine, type BlockIndex, type DiagnosticAction,
  type DiagnosticState,
} from "./flow"

/**
 * Les écrans du diagnostic. Un choix simple par écran : le toucher suffit, on passe à
 * l'écran suivant (`pick`). Les écrans à plusieurs valeurs ont le bouton « Continuer »
 * du pied de page (voir DiagnosticFlow).
 */

type Pick = (action: DiagnosticAction) => void

interface ScreenProps {
  s: DiagnosticState
  dispatch: Dispatch<DiagnosticAction>
  pick: Pick
}

// ── 0 — Introduction ──────────────────────────────────────────────────────
export function IntroScreen({ onStart }: { onStart: () => void }) {
  return (
    <section className="zd-screen" style={{ paddingTop: 48, gap: 22 }}>
      <div className="zd-brand">
        <span className="zd-logo-mark"><img src="/brand/zeyone-mark.png" alt="" /></span>
        <span className="zd-logo-word">ZEY<span className="zd-accent">ONE</span></span>
      </div>
      <div style={{ marginTop: 28 }}>
        <Eyebrow>Ton diagnostic · 2 min</Eyebrow>
      </div>
      <ScreenTitle xl>Je pars de là où <span className="zd-accent">tu en es.</span></ScreenTitle>
      <p className="zd-lead" style={{ fontSize: 18, lineHeight: 1.55, margin: 0 }}>
        Trois blocs, 6 à 9 questions. Avec tes réponses, je choisis ton programme et je le place dans ton calendrier.
      </p>
      <ol className="zd-card zd-steps">
        <li><span>1-</span><span>Toi</span></li>
        <li><span>2-</span><span>Ta pratique</span></li>
        <li><span>3-</span><span>Ton objectif</span></li>
      </ol>
      <div style={{ flex: "1 1 auto" }} />
      <div className="zd-actions">
        <button type="button" className="zd-btn zd-btn-primary" onClick={onStart}>Commencer mon diagnostic</button>
      </div>
    </section>
  )
}

// ── 0b — Prénom (seulement si le compte n'en a pas) ────────────────────────
export function FirstNameScreen({ s, dispatch }: ScreenProps) {
  return (
    <section className="zd-screen">
      <ScreenTitle>Comment tu t'appelles ?</ScreenTitle>
      <label htmlFor="zd-prenom" className="zd-label">Ton prénom</label>
      <input
        id="zd-prenom"
        className="zd-input"
        type="text"
        autoComplete="given-name"
        maxLength={40}
        value={s.firstName}
        onChange={(e) => dispatch({ type: "setFirstName", value: e.target.value })}
        placeholder="Ton prénom"
      />
    </section>
  )
}

// ── 1 — Genre ─────────────────────────────────────────────────────────────
export function GenderScreen({ s, pick }: ScreenProps) {
  return (
    <section className="zd-screen">
      <ScreenTitle>Tu es…</ScreenTitle>
      <Lead>Ton programme n'est pas le même pour un homme et pour une femme.</Lead>
      {(["male", "female"] as const).map((g) => (
        <Choice key={g} selected={s.gender === g} onClick={() => pick({ type: "setGender", value: g })}>
          <span className="zd-choice-title" style={{ fontSize: 28 }}>{GENDER_LABELS[g]}</span>
          {s.gender === g && <CheckMark />}
        </Choice>
      ))}
    </section>
  )
}

// ── 2 — Ton point de départ ───────────────────────────────────────────────
export function MetricsScreen({ s, dispatch }: ScreenProps) {
  return (
    <section className="zd-screen">
      <ScreenTitle>Ton point de départ</ScreenTitle>
      <Lead>Je m'en sers pour ajuster ton programme, et c'est à ce poids que je comparerai tes prochaines pesées.</Lead>
      {METRICS.map((m) => (
        <NumberWheel
          key={m.key}
          id={`zd-${m.key}`}
          label={m.label}
          unit={m.unit}
          min={m.min}
          max={m.max}
          value={s[m.key]}
          onChange={(v) => dispatch({ type: "setMetric", key: m.key, value: v })}
        />
      ))}
    </section>
  )
}

// ── 3 — Niveau ────────────────────────────────────────────────────────────
export function LevelScreen({ s, pick }: ScreenProps) {
  return (
    <section className="zd-screen" style={{ gap: 12 }}>
      <ScreenTitle>Où tu en es en musculation ?</ScreenTitle>
      <Lead>Ton niveau fixe ton programme et tes premières charges.</Lead>
      {LEVELS.map((l) => (
        <Choice
          key={l.id}
          className="zd-choice-col"
          selected={s.levelId === l.id}
          onClick={() => pick({ type: "setLevel", value: l.id })}
        >
          <span className="zd-bars" aria-hidden="true">
            {[1, 2, 3].map((n) => <span key={n} className="zd-bar" data-on={n <= l.bars} />)}
          </span>
          <span className="zd-choice-title">{l.title}</span>
          <span className="zd-choice-text">{l.description}</span>
        </Choice>
      ))}
    </section>
  )
}

// ── 3b — Expérience Débutant (libellés et valeurs inchangés) ──────────────
export function ExperienceScreen({ s, pick }: ScreenProps) {
  return (
    <section className="zd-screen" style={{ gap: 12 }}>
      <ScreenTitle>Tu as déjà pratiqué la musculation ?</ScreenTitle>
      <Lead>Ça me sert à calibrer tes premiers objectifs de force.</Lead>
      {BEGINNER_EXPERIENCE_OPTIONS.map((o) => (
        <Choice
          key={o.id}
          className="zd-choice-plain"
          selected={s.beginnerExperience === o.id}
          onClick={() => pick({ type: "setBeginnerExperience", value: o.id })}
        >
          <span>{o.label}</span>
          {s.beginnerExperience === o.id && <CheckMark />}
        </Choice>
      ))}
    </section>
  )
}

// ── 4 — Lieu ──────────────────────────────────────────────────────────────
export function PlaceScreen({ s, pick }: ScreenProps) {
  return (
    <section className="zd-screen">
      <ScreenTitle>Où tu t'entraînes ?</ScreenTitle>
      <Lead>Je construis tes séances avec le matériel que tu as vraiment.</Lead>
      {PLACES.map((p) => {
        const disabled = p.id === "home" && s.levelId === "advanced"
        return (
          <Choice
            key={p.id}
            className="zd-choice-col"
            selected={s.place === p.id}
            disabled={disabled}
            onClick={() => pick({ type: "setPlace", value: p.id })}
          >
            <span className="zd-choice-title" style={{ fontSize: 28 }}>{p.title}</span>
            {disabled && <span className="zd-choice-text">{ADVANCED_HOME_NOTE}</span>}
          </Choice>
        )
      })}
    </section>
  )
}

// ── 5 — Blessure : oui / non ──────────────────────────────────────────────
export function InjuryScreen({ s, pick }: ScreenProps) {
  return (
    <section className="zd-screen">
      <ScreenTitle>Tu as déjà eu une blessure ?</ScreenTitle>
      <Lead>Entorse, tendinite, déchirure, opération, mal de dos qui revient… Même ancienne, dis-le-moi.</Lead>
      {([["non", "Non, jamais"], ["oui", "Oui"]] as const).map(([id, label]) => (
        <Choice key={id} selected={s.injury === id} onClick={() => pick({ type: "setInjury", value: id })}>
          <span className="zd-choice-title">{label}</span>
        </Choice>
      ))}
      <p className="zd-note">
        Je te demanderai ton accord avant d'enregistrer quoi que ce soit. Dans l'app, seuls toi et moi, ton coach, voyons ces informations.
      </p>
    </section>
  )
}

// ── 6 — Zones ─────────────────────────────────────────────────────────────
export function ZonesScreen({ s, dispatch }: ScreenProps) {
  return (
    <section className="zd-screen">
      <ScreenTitle>Où ça ?</ScreenTitle>
      <Lead>Tu peux en choisir plusieurs.</Lead>
      <div className="zd-chips">
        {INJURY_ZONES.map((z) => (
          <button
            key={z.id}
            type="button"
            className="zd-chip"
            aria-pressed={s.zones.includes(z.id as InjuryZone)}
            onClick={() => dispatch({ type: "toggleZone", zone: z.id as InjuryZone })}
          >
            {z.label}
          </button>
        ))}
      </div>
      {s.zones.includes("autre") && (
        <>
          <label htmlFor="zd-autre" className="zd-label">Autre : laquelle ?</label>
          <input
            id="zd-autre"
            className="zd-input"
            type="text"
            maxLength={50}
            value={s.zoneOther}
            onChange={(e) => dispatch({ type: "setZoneOther", value: e.target.value })}
            placeholder="Ex. : mâchoire, côtes…"
          />
        </>
      )}
    </section>
  )
}

// ── 7 — État, précision, accord ───────────────────────────────────────────
export function InjuryStatusScreen({ s, dispatch, onSkip }: ScreenProps & { onSkip: () => void }) {
  const [showDetails, setShowDetails] = useState(false)
  const detailsId = useId()
  return (
    <section className="zd-screen">
      <ScreenTitle>Et aujourd'hui ?</ScreenTitle>
      {s.zones.map((zone) => (
        <div key={zone} className="zd-card zd-zone-card" role="group" aria-label={getInjuryZoneLabel(zone, s.zoneOther)}>
          <span className="zd-zone-title">{getInjuryZoneLabel(zone, s.zoneOther)}</span>
          {(["guerie", "genante"] as const).map((st) => (
            <button
              key={st}
              type="button"
              className="zd-chip zd-chip-wide"
              aria-pressed={s.statuses[zone] === st}
              onClick={() => dispatch({ type: "setStatus", zone, status: st })}
            >
              {INJURY_STATUS_LABELS[st]}
            </button>
          ))}
        </div>
      ))}

      <label htmlFor="zd-note" className="zd-label">
        Tu veux préciser ? <span className="zd-label-faint">(facultatif)</span>
      </label>
      <textarea
        id="zd-note"
        className="zd-textarea"
        rows={2}
        maxLength={500}
        value={s.injuryNote}
        onChange={(e) => dispatch({ type: "setInjuryNote", value: e.target.value })}
        placeholder="Ex. : entorse du genou droit en 2023, opérée"
      />

      <div className="zd-consent">
        <div className="zd-consent-row">
          <input
            id="zd-consent"
            type="checkbox"
            checked={s.injuryConsent}
            onChange={(e) => dispatch({ type: "setInjuryConsent", value: e.target.checked })}
          />
          <label htmlFor="zd-consent">{INJURY_CONSENT_LABEL}</label>
        </div>
        <button
          type="button"
          className="zd-consent-more"
          aria-expanded={showDetails}
          aria-controls={detailsId}
          onClick={() => setShowDetails((v) => !v)}
        >
          {showDetails ? "Masquer les détails" : "Détails"}
        </button>
        <p id={detailsId} className="zd-consent-details" hidden={!showDetails}>
          {INJURY_CONSENT_DETAILS}{" "}
          <a href="/privacy" target="_blank" rel="noopener noreferrer">Politique de confidentialité</a>
        </p>
      </div>

      <div className="zd-skip">
        <button type="button" className="zd-link-dashed" onClick={onSkip}>Continuer sans enregistrer mes blessures</button>
        <span>Ton programme ne pourra pas en tenir compte.</span>
      </div>
      {!allZonesHaveStatus(s) && (
        <p className="zd-sr-only" aria-live="polite">Indique l'état de chaque zone pour continuer.</p>
      )}
    </section>
  )
}

// ── 8 — Objectif ──────────────────────────────────────────────────────────
export function GoalScreen({ s, pick }: ScreenProps) {
  const goals = GOALS_BY_LEVEL[s.levelId ?? "beginner"]
  return (
    <section className="zd-screen" style={{ gap: 12 }}>
      <ScreenTitle>Ton objectif principal ?</ScreenTitle>
      <Lead>Un seul : c'est lui qui oriente tout ton programme.</Lead>
      {goals.map((g) => (
        <Choice key={g.id} selected={s.goalId === g.id} onClick={() => pick({ type: "setGoal", value: g.id })}>
          <span className="zd-choice-title" style={{ fontSize: 25 }}>{g.title}</span>
          {s.goalId === g.id && <CheckMark />}
        </Choice>
      ))}
    </section>
  )
}

// ── 9 — Écran de fin ──────────────────────────────────────────────────────
export function EndScreen({
  s, displayName, texts, saving, trialBlocked, onStart, onSeeOffers, onEditBlock,
}: {
  s: DiagnosticState
  displayName: string
  texts: ParcoursTexts
  saving: boolean
  trialBlocked: boolean
  onStart: () => void
  onSeeOffers: () => void
  onEditBlock: (block: BlockIndex) => void
}) {
  const [reviewOpen, setReviewOpen] = useState(false)
  return (
    <section className="zd-screen" style={{ paddingTop: 22, gap: 16 }}>
      <Eyebrow>Diagnostic terminé</Eyebrow>
      <ScreenTitle xl>J'ai ton programme, <span className="zd-accent">{displayName}.</span></ScreenTitle>

      <div className="zd-programme">
        <span className="zd-programme-name">{programmeLine(s)}</span>
        <p>4 séances par semaine, placées dans ton calendrier dès que tu démarres.</p>
        <button type="button" className="zd-link-dashed zd-link-accent" style={{ alignSelf: "flex-start" }} onClick={() => setReviewOpen(true)}>
          Revoir mes réponses
        </button>
      </div>

      {hasActivePain(s) && (
        <div className="zd-notice">
          <span className="zd-notice-eyebrow">{PAIN_RECOMMENDATION.eyebrow}</span>
          <p><strong>{PAIN_RECOMMENDATION.lead}</strong> {PAIN_RECOMMENDATION.body}</p>
          <p>{PAIN_RECOMMENDATION.reassurance}</p>
          <a href={mailtoHref(PAIN_RECOMMENDATION.mailSubject, PAIN_RECOMMENDATION.mailBody)}>{PAIN_RECOMMENDATION.cta} →</a>
        </div>
      )}

      <div style={{ flex: "1 1 auto" }} />

      {trialBlocked ? (
        <div className="zd-warning" role="alert">
          <p>{TRIAL_ALREADY_USED}</p>
          <button type="button" className="zd-btn zd-btn-primary" onClick={onSeeOffers}>Voir les accompagnements</button>
        </div>
      ) : (
        <div className="zd-actions">
          <button type="button" className="zd-btn zd-btn-primary" onClick={onStart} disabled={saving} aria-busy={saving}>
            {saving ? texts.finCtaBusy : texts.finCta}
          </button>
          <span className="zd-foot">{texts.finFoot}</span>
        </div>
      )}

      {reviewOpen && (
        <ReviewSheet
          s={s}
          onClose={() => setReviewOpen(false)}
          onEdit={(b) => { setReviewOpen(false); onEditBlock(b) }}
        />
      )}
    </section>
  )
}

function injuriesSummary(s: DiagnosticState): string {
  if (s.injury !== "oui") return "Aucune"
  if (s.injuriesSkipped) return "Non enregistrées (à ta demande)"
  return s.zones
    .map((z) => `${getInjuryZoneLabel(z, s.zoneOther)} : ${s.statuses[z] === "genante" ? "me fait encore mal ou me gêne" : "guérie"}`)
    .join("\n")
}

function ReviewSheet({ s, onClose, onEdit }: { s: DiagnosticState; onClose: () => void; onEdit: (b: BlockIndex) => void }) {
  const closeRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    closeRef.current?.focus()
    const onKey = (e: globalThis.KeyboardEvent) => { if (e.key === "Escape") onClose() }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [onClose])

  const level = LEVELS.find((l) => l.id === s.levelId)?.title ?? "—"
  const place = PLACES.find((p) => p.id === s.place)?.short ?? "—"
  const goal = s.levelId ? GOALS_BY_LEVEL[s.levelId].find((g) => g.id === s.goalId)?.title ?? "—" : "—"
  const rows: { block: BlockIndex; label: string; value: string }[] = [
    { block: 0, label: "Toi", value: `${s.gender ? GENDER_LABELS[s.gender] : "—"} · ${s.age} ans · ${s.height} cm · ${s.weight} kg` },
    { block: 1, label: "Ta pratique", value: `${level} · ${place}\nBlessures :\n${injuriesSummary(s)}` },
    { block: 2, label: "Ton objectif", value: goal },
  ]

  // Portail vers <body> : le panneau est fixé à l'écran, et un `position: fixed` placé
  // dans l'écran animé (transform) se positionnerait par rapport à lui, pas à la page.
  return createPortal(
    <div className="zd-portal zd-sheet-backdrop" onClick={onClose}>
      <div className="zd-sheet" role="dialog" aria-modal="true" aria-labelledby="zd-review-title" onClick={(e) => e.stopPropagation()}>
        <span className="zd-sheet-handle" aria-hidden="true" />
        <h2 id="zd-review-title" className="zd-sheet-title">Tes réponses</h2>
        <div>
          {rows.map((r) => (
            <div key={r.label} className="zd-review-row">
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span className="zd-review-label">{r.label}</span>
                <span className="zd-review-value">{r.value}</span>
              </div>
              <button type="button" className="zd-review-edit" onClick={() => onEdit(r.block)} aria-label={`Modifier : ${r.label}`}>
                Modifier
              </button>
            </div>
          ))}
        </div>
        <button ref={closeRef} type="button" className="zd-btn zd-btn-ghost" style={{ marginTop: 4, minHeight: 50 }} onClick={onClose}>
          Fermer
        </button>
      </div>
    </div>,
    document.body,
  )
}
